import { useState } from "react";
import { useContracts } from "@/hooks/useContracts";
import { MyCatsStrip } from "@/components/sections/MyCatsStrip";
import { CatPreviewCard } from "@/components/operations/CatPreviewCard";
import { ActivatePanel } from "@/components/operations/ActivatePanel";
import { UpgradePanel } from "@/components/operations/UpgradePanel";
import { SplitPanel } from "@/components/operations/SplitPanel";
import { ClaimPanel } from "@/components/operations/ClaimPanel";
import { FusePanel } from "@/components/operations/FusePanel";
import { cn } from "@/lib/utils";
import { Zap, Flame, SlidersHorizontal, Wallet, Combine } from "lucide-react";

type TabKey = "activate" | "upgrade" | "split" | "claim" | "fuse";

const tabs: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: "activate", label: "Activate", icon: Zap },
  { key: "upgrade", label: "Upgrade", icon: Flame },
  { key: "split", label: "Split", icon: SlidersHorizontal },
  { key: "claim", label: "Claim", icon: Wallet },
  { key: "fuse", label: "Fuse", icon: Combine },
];

export function OperationsDashboard() {
  const [selectedTokenId, setSelectedTokenId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("activate");
  const [refreshTick, setRefreshTick] = useState(0);

  const { connected } = useContracts();

  // 链上数据由各面板在交易成功后统一调用 refreshAfterTx 刷新，
  // 这里只负责重挂载面板以重置其内部表单状态
  const triggerRefresh = () => {
    setRefreshTick((t) => t + 1);
  };

  const renderPanel = () => {
    switch (activeTab) {
      case "activate":
        return (
          <ActivatePanel
            key={`activate-${refreshTick}`}
            tokenId={selectedTokenId}
            onDone={triggerRefresh}
          />
        );
      case "upgrade":
        return (
          <UpgradePanel
            key={`upgrade-${refreshTick}`}
            tokenId={selectedTokenId}
            onDone={triggerRefresh}
          />
        );
      case "split":
        return (
          <SplitPanel
            key={`split-${refreshTick}`}
            tokenId={selectedTokenId}
            onDone={triggerRefresh}
          />
        );
      case "claim":
        return (
          <ClaimPanel
            key={`claim-${refreshTick}`}
            tokenId={selectedTokenId}
            onDone={triggerRefresh}
          />
        );
      case "fuse":
        return (
          <FusePanel
            key={`fuse-${refreshTick}`}
            tokenId={selectedTokenId}
            onDone={triggerRefresh}
          />
        );
      default:
        return null;
    }
  };

  return (
    <section className="bg-card/30 py-12 md:py-16">
      <div className="container mx-auto max-w-6xl px-4">
        <div className="mb-8 max-w-2xl">
          <p className="font-pixel text-xs text-[var(--accent-green)]">COMMAND CENTER</p>
          <h2 className="mt-2 font-pixel text-2xl text-foreground md:text-3xl">
            MANAGE YOUR CATS.
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Select a cat, then use the tabs below to activate, upgrade, split rewards, fuse, or claim.
          </p>
        </div>

        <MyCatsStrip
          selectedId={selectedTokenId}
          onSelect={(id) => {
            setSelectedTokenId(id);
            if (!connected) return;
            // 默认切换到该猫最可能需要做的操作
            setActiveTab("activate");
          }}
        />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <CatPreviewCard tokenId={selectedTokenId} />
          </div>

          <div className="lg:col-span-8">
            <div className="flex flex-wrap gap-2 border-b border-border">
              {tabs.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={cn(
                    "flex items-center gap-2 rounded-t-lg border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                    activeTab === key
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>

            <div className="rounded-b-2xl rounded-tr-2xl border border-t-0 border-border bg-card p-6 shadow-sm">
              {renderPanel()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
